import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import EmptyState from "@/components/base/EmptyState";
import { getContentById, getRelatedContent, kindName } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";

const shareTargets = [
  { key: "wechat", label: "微信", icon: "ri-wechat-line" },
  { key: "weibo", label: "微博", icon: "ri-weibo-line" },
  { key: "telegram", label: "Telegram", icon: "ri-telegram-line" },
  { key: "copy", label: "复制链接", icon: "ri-link" },
];

export default function DetailPage() {
  const { id } = useParams();
  const detail = getContentById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  useDocumentMeta({
    title: detail ? `${detail.title} - 18禁` : "内容不存在 - 18禁",
    description: detail
      ? `${detail.summary}（18禁内容演示，仅面向成年读者。）`
      : "没有找到对应的内容，可能已下架或链接有误。",
    keywords: detail ? `${detail.tags.join(",")},18禁,成人内容` : "18禁,成人内容",
  });

  useEffect(() => {
    setShareOpen(false);
    setToast("");
  }, [id]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!detail) {
    return (
      <div className="mx-auto max-w-[860px] px-4 py-16 md:px-6 md:py-24">
        <EmptyState
          title="内容不存在或已下线"
          description="这个链接可能已经失效，或者对应的内容已从演示数据中移除。你可以回到资讯或资源列表继续浏览。"
          actionLabel="查看全部资讯"
          onAction={() => {
            window.REACT_APP_NAVIGATE?.("/news");
          }}
          secondaryLabel="返回首页"
          secondaryTo="/"
        />
      </div>
    );
  }

  const related = getRelatedContent(detail);
  const favorited = isFavorite(detail.id);

  const handleShare = (key: string) => {
    if (key === "copy") {
      try {
        navigator.clipboard?.writeText(window.location.href);
      } catch {
        // clipboard may be unavailable, still show the demo feedback
      }
    }
    setToast(key === "copy" ? "链接已复制（演示）" : `已生成${shareTargets.find((item) => item.key === key)?.label}分享卡片（演示）`);
    setShareOpen(false);
  };

  return (
    <div>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[900px] px-4 py-5 md:px-6">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
            <Link to={detail.backTo} className="cursor-pointer transition hover:text-primary-700">
              {detail.backLabel.replace("返回", "")}
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
            <span className="line-clamp-1 text-foreground-700">{kindName(detail.kind)}详情</span>
          </nav>
        </div>
      </div>

      <article className="mx-auto max-w-[900px] px-4 py-10 md:px-6 md:py-14">
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <Badge tone="primary">{detail.category}</Badge>
          <Badge tone="secondary">{kindName(detail.kind)}</Badge>
          <span className="text-xs text-foreground-500">{detail.date}</span>
        </div>

        <h1 className="font-heading text-2xl leading-[1.35] text-foreground-950 md:text-[36px]">{detail.title}</h1>
        <p className="mt-4 text-sm leading-[1.9] text-foreground-600 md:text-[15px]">{detail.summary}</p>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-background-200 py-3.5">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line text-sm" aria-hidden="true"></i>
              {detail.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-building-line text-sm" aria-hidden="true"></i>
              {detail.source}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line text-sm" aria-hidden="true"></i>
              演示数据
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const next = toggleFavorite(detail.id);
                setToast(next ? "已加入收藏（保存在本地）" : "已取消收藏");
              }}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3.5 py-2 text-xs transition ${
                favorited
                  ? "border-accent-400 bg-accent-100 text-accent-800"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:border-accent-300"
              }`}
              aria-pressed={favorited}
            >
              <i className={favorited ? "ri-bookmark-fill text-sm" : "ri-bookmark-line text-sm"} aria-hidden="true"></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-600 transition hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-share-forward-line text-sm" aria-hidden="true"></i>
              分享
            </button>
          </div>
        </div>

        {detail.cover ? (
          <figure className="mt-8 overflow-hidden rounded-lg bg-background-200">
            <div className="relative w-full" style={{ aspectRatio: detail.kind === "resource" ? "16 / 10" : "16 / 9" }}>
              <img
                src={detail.cover}
                alt={`${detail.title} 封面`}
                title={`${detail.title} - 18禁`}
                className="h-full w-full object-top object-cover"
              />
            </div>
            <figcaption className="border-t border-background-200 bg-background-50 px-4 py-2 text-[11px] text-foreground-500">
              图片为虚构演示封面，用于展示版式效果
            </figcaption>
          </figure>
        ) : (
          <div className="mt-8 flex flex-col justify-between overflow-hidden rounded-lg bg-primary-800 px-6 py-8 text-background-100 md:px-8 md:py-10">
            <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] text-accent-300">
              <span className="flex h-5 items-center rounded-sm bg-accent-500 px-1.5 text-[10px] font-bold text-foreground-950">
                18+
              </span>
              DISCUSSION THREAD
            </div>
            <p className="mt-6 max-w-lg font-heading text-lg leading-relaxed text-background-50 md:text-xl">
              「{detail.title}」
            </p>
            <p className="mt-3 text-xs text-background-200/80">话题由读者发起 · 内容为虚构演示</p>
          </div>
        )}

        <div className="mt-9 article-body text-sm md:text-[15px]">
          {detail.body.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        <div className="mt-9 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
          <span className="text-[11px] tracking-[0.16em] text-foreground-400">TAGS</span>
          {detail.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-800">
              #{tag}
            </span>
          ))}
        </div>

        <div className="mt-7 rounded-lg border border-primary-200/70 bg-primary-50 px-4 py-3.5">
          <p className="text-[11px] leading-relaxed text-primary-800">
            <strong className="font-semibold">内容声明：</strong>
            本页作品、人物、评分与图片来源均为虚构演示，仅用于展示产品结构与阅读体验。本站不提供资源下载、播放或任何外部跳转。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to={detail.backTo}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line text-base" aria-hidden="true"></i>
            {detail.backLabel}
          </Link>
          <Link
            to="/"
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-700"
          >
            返回首页
          </Link>
        </div>
      </article>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-[900px] px-4 py-12 md:px-6 md:py-14">
          <div className="mb-6 flex items-end justify-between gap-3">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="h-px w-6 bg-accent-500"></span>
                <span className="text-[11px] font-medium tracking-[0.18em] text-accent-700">RELATED</span>
              </div>
              <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">相关内容推荐</h2>
            </div>
            <span className="hidden text-[11px] text-foreground-500 sm:block">基于分类与标签关联</span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex cursor-pointer items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition hover:border-primary-200"
              >
                <div className="h-[64px] w-[64px] shrink-0 overflow-hidden rounded-md bg-background-200">
                  {item.cover ? (
                    <img
                      src={item.cover}
                      alt={`${item.title} 缩略图`}
                      title={`${item.title} - 18禁相关内容`}
                      className="h-full w-full object-top object-cover transition duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-primary-100 text-primary-600">
                      <i className="ri-discuss-line text-lg" aria-hidden="true"></i>
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-[10px] tracking-wide text-foreground-400">{kindName(item.kind)}</span>
                    <span className="text-[10px] text-background-400">·</span>
                    <span className="text-[10px] text-foreground-400">{item.date}</span>
                  </div>
                  <h3 className="line-clamp-2 text-[13px] font-medium leading-snug text-foreground-900 transition group-hover:text-primary-700">
                    {item.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {shareOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm animate-fade-up rounded-lg bg-background-50 p-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950">分享这篇内容</h3>
                <p className="mt-1 text-[11px] text-foreground-500">分享功能为界面演示，不会调用外部接口</p>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                aria-label="关闭分享弹窗"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition hover:bg-background-100"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {shareTargets.map((target) => (
                <button
                  key={target.key}
                  type="button"
                  onClick={() => handleShare(target.key)}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-background-200 bg-background-100/60 px-3 py-3 text-xs text-foreground-700 transition hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={`${target.icon} text-base`} aria-hidden="true"></i>
                  {target.label}
                </button>
              ))}
            </div>
            <p className="mt-4 truncate rounded-md bg-background-100 px-3 py-2 text-[11px] text-foreground-500">
              {detail.title}
            </p>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950 px-5 py-2.5 text-xs text-background-50">
          {toast}
        </div>
      ) : null}
    </div>
  );
}